import { useState, useEffect, useCallback } from "react";
import { C, S } from "../customers/theme";
import { getDeliveryNotes, getDeliveryNote, createDeliveryNote, updateDeliveryNote, deleteDeliveryNote, getDispatchableSalesOrders, DN_STATUSES } from "../lib/deliveryNotes";
import { listCustomers } from "../lib/customers";
import DeliveryNoteForm from "./DeliveryNoteForm";
import DeliveryNoteView, { DNStatusBadge } from "./DeliveryNoteView";

const PAGE_SIZE = 25;

function Th({ label, col, sortBy, sortDir, onSort }) {
  const active = sortBy === col;
  return (
    <th style={S.th} aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}>
      <button onClick={() => onSort(col)} style={{ background: "none", border: "none", color: "inherit", font: "inherit", cursor: "pointer", padding: 0, display: "inline-flex", alignItems: "center", gap: 4, textTransform: "inherit", letterSpacing: "inherit" }}>
        {label}<span style={{ opacity: active ? 1 : 0.25, fontSize: 10 }}>{active ? (sortDir === "asc" ? "▲" : "▼") : "↕"}</span>
      </button>
    </th>
  );
}

export default function DeliveryNoteManager({ autoNew, onAutoNew }) {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [customerId, setCustomerId] = useState("all");
  const [sortBy, setSortBy] = useState("created");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);

  const [dispatchableSOs, setDispatchableSOs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { const { data, count: total } = await getDeliveryNotes({ search, status, customerId, page, pageSize: PAGE_SIZE, sortBy, sortDir }); setRows(data); setCount(total); }
    catch (e) { setError(e.message || "Could not load delivery notes."); }
    finally { setLoading(false); }
  }, [search, status, customerId, sortBy, sortDir, page]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const loadRefs = useCallback(async () => {
    try { const { data } = await getDispatchableSalesOrders(); setDispatchableSOs(data); } catch { /* surfaced via list */ }
    try { setCustomers(await listCustomers({ status: "active" })); } catch { /* filter degrades */ }
  }, []);
  useEffect(() => { loadRefs(); }, [loadRefs]);

  useEffect(() => {
    if (autoNew) { setEditing({}); onAutoNew && onAutoNew(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoNew]);

  const onSearch = (v) => { setSearch(v); setPage(1); };
  const onStatus = (v) => { setStatus(v); setPage(1); };
  const onCustomer = (v) => { setCustomerId(v); setPage(1); };
  const onSort = (col) => { setPage(1); if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc")); else { setSortBy(col); setSortDir("asc"); } };
  const clearFilters = () => { setSearch(""); setStatus("all"); setCustomerId("all"); setPage(1); };

  const openNew = () => setEditing({});
  const openEdit = async (d) => { try { setEditing(await getDeliveryNote(d.id)); } catch (e) { setError(e.message); } };
  const openView = async (d) => { try { setViewing(await getDeliveryNote(d.id)); } catch (e) { setError(e.message); } };

  const save = async (payload) => {
    if (editing && editing.id) await updateDeliveryNote(editing.id, payload);
    else await createDeliveryNote(payload);
    setEditing(null);
    await Promise.all([load(), loadRefs()]); // refresh list + dispatchable SOs (reservations changed)
  };
  const del = async (d) => {
    if (!window.confirm(`Delete ${d.dn_number}? Dispatched stock will be restored and reservations recalculated.`)) return;
    try { await deleteDeliveryNote(d.id); await Promise.all([load(), loadRefs()]); } catch (e) { setError(e.message); }
  };

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const from = count === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, count);
  const hasFilters = !!search || status !== "all" || customerId !== "all";
  const selStyle = { ...S.input, width: "auto", minWidth: 150 };

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <input style={S.searchBar} placeholder="Search DN no., customer, SO, driver…" value={search} onChange={(e) => onSearch(e.target.value)} aria-label="Search delivery notes" />
        <select style={selStyle} value={customerId} onChange={(e) => onCustomer(e.target.value)} aria-label="Filter by customer">
          <option value="all">All customers</option>
          {customers.map((cu) => <option key={cu.id} value={cu.id}>{cu.company_name}</option>)}
        </select>
        <select style={selStyle} value={status} onChange={(e) => onStatus(e.target.value)} aria-label="Filter by status">
          <option value="all">All statuses</option>
          {DN_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        <span style={{ color: C.textMuted, fontSize: 12 }}>{count} note{count === 1 ? "" : "s"}</span>
        <button style={S.btn()} onClick={openNew}>+ New Delivery Note</button>
      </div>

      {error && (
        <div role="alert" style={{ display: "flex", alignItems: "center", gap: 12, background: C.dangerDim, border: `1px solid ${C.danger}`, color: C.text, borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>
          <span style={{ color: C.danger, fontWeight: 700 }}>⚠ {error}</span>
          <button style={S.btn("secondary")} onClick={load}>Retry</button>
        </div>
      )}

      <div style={S.card}>
        <table style={S.table}>
          <thead>
            <tr>
              <Th label="DN Number" col="number" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <th style={S.th}>Customer</th>
              <th style={S.th}>Sales Order</th>
              <Th label="Delivery Date" col="date" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <Th label="Status" col="status" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <th style={S.th} aria-label="Actions"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={S.empty}>Loading…</td></tr>
            ) : count === 0 ? (
              <tr><td colSpan={6} style={S.empty}>
                {hasFilters ? (
                  <div>No delivery notes match your filters.<div style={{ marginTop: 10 }}><button style={S.btn("secondary")} onClick={clearFilters}>Clear filters</button></div></div>
                ) : (
                  <div>
                    <div style={{ fontWeight: 600, color: C.text, marginBottom: 4 }}>No delivery notes yet</div>
                    <div style={{ marginBottom: 12 }}>Dispatch a sales order's reserved stock to get started.</div>
                    <button style={S.btn()} onClick={openNew}>+ New Delivery Note</button>
                  </div>
                )}
              </td></tr>
            ) : rows.map((d) => (
              <tr key={d.id} style={{ cursor: "pointer" }} onClick={() => openView(d)}>
                <td style={S.td}><span style={{ color: C.textMuted, fontFamily: "monospace", fontSize: 12 }}>{d.dn_number}</span></td>
                <td style={S.td}>{d.customer_name || d.customers?.company_name || <span style={{ color: C.textDim }}>—</span>}</td>
                <td style={S.td}><span style={{ fontFamily: "monospace", fontSize: 12 }}>{d.sales_order_number || d.sales_orders?.so_number || "—"}</span></td>
                <td style={S.td}>{d.delivery_date || "—"}</td>
                <td style={S.td}><DNStatusBadge status={d.status} /></td>
                <td style={S.td} onClick={(e) => e.stopPropagation()}>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button style={S.btn("ghost")} title="View" aria-label={`View ${d.dn_number}`} onClick={() => openView(d)}>👁</button>
                    <button style={S.btn("ghost")} title="Edit" aria-label={`Edit ${d.dn_number}`} onClick={() => openEdit(d)}>✎</button>
                    <button style={S.btn("ghost")} title="Delete" aria-label={`Delete ${d.dn_number}`} onClick={() => del(d)}>🗑</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {count > 0 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 14, flexWrap: "wrap", gap: 10 }}>
            <span style={{ color: C.textMuted, fontSize: 12 }}>Showing {from}–{to} of {count}</span>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button style={{ ...S.btn("secondary"), opacity: page <= 1 ? 0.5 : 1 }} disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} aria-label="Previous page">← Prev</button>
              <span style={{ color: C.textMuted, fontSize: 12 }}>Page {page} of {totalPages}</span>
              <button style={{ ...S.btn("secondary"), opacity: page >= totalPages ? 0.5 : 1 }} disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} aria-label="Next page">Next →</button>
            </div>
          </div>
        )}
      </div>

      {editing !== null && (
        <DeliveryNoteForm initial={editing} dispatchableSOs={dispatchableSOs} onCancel={() => setEditing(null)} onSave={save} />
      )}
      {viewing && (
        <DeliveryNoteView note={viewing} onClose={() => setViewing(null)} onEdit={(d) => { setViewing(null); openEdit(d); }} />
      )}
    </div>
  );
}
